import React, { useState, useMemo } from 'react'
import {
  Box,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import WarningIcon from '@mui/icons-material/Warning'
import {
  AutoCoderFramework,
  AutoCoderCode,
  AutoCoderImportType,
  FrameworkEngineConfig,
  CarbonInterviewRawDataRow,
  SourceVariable,
  ChatMessage as GoChatMessage,
} from '../../../types/gotypes'
import { FrameworkData } from '../../../hooks/useFrameworkLibrary'
import { useThemeAI } from '../../../hooks/useThemeAI'
import useAutoCoder from '../../../hooks/useAutoCoder'
import axios from 'axios'
import { SidePaneChat } from '../../../agent'
import { toSidePaneMessages } from '../../chat/adapters'
import type { ChatMessage } from '../../chat/types'
import { buildTranscribeEndpoint } from '../../../hooks/useTranscribeEndpoint'
import useDictationAccess from '../../../hooks/useDictationAccess'
import ThemeEditor from '../../theme-ai/ThemeEditor'
import { buildColorMap } from '../colorPalette'
import DataPreviewTable from './DataPreviewTable'
import { DEFAULT_LOOPED_VARIABLES } from './builtinCodeFrames'

interface ThemeAIModalProps {
  open: boolean
  onCancel: () => void
  onApply: (framework: AutoCoderFramework) => void
  onSaveToLibrary?: (payload: { name: string; description?: string; data: FrameworkData }) => Promise<void>
  saveToLibraryLoading?: boolean
  frameworkEngine?: FrameworkEngineConfig
  onFrameworkEngineChange?: (config: FrameworkEngineConfig) => void
  hasDataConfirmed: boolean
  previewData: CarbonInterviewRawDataRow[]
  variable?: SourceVariable
  customer: string
  job: string
  pipelineId: string
  disabled?: boolean
  /** Existing looped variables to preserve when applying */
  existingLoopedVariables?: AutoCoderFramework['looped_variables']
}

const ThemeAIModal: React.FC<ThemeAIModalProps> = ({
  open,
  onCancel,
  onApply,
  onSaveToLibrary,
  saveToLibraryLoading,
  frameworkEngine,
  onFrameworkEngineChange,
  hasDataConfirmed,
  previewData,
  variable,
  customer,
  job,
  pipelineId,
  disabled,
  existingLoopedVariables,
}) => {
  // Save to library dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveLibraryName, setSaveLibraryName] = useState('')
  const [saveLibraryDescription, setSaveLibraryDescription] = useState('')

  // Pagination state for records preview
  const [recordsPage, setRecordsPage] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(100)

  // Track initialization from saved config
  const [initializedFromConfig, setInitializedFromConfig] = useState(false)

  // Fetch records from API
  const autoCoder = useAutoCoder({
    customer,
    job,
    pipelineId: pipelineId === 'new' ? undefined : pipelineId,
  })

  React.useEffect(() => {
    autoCoder.setRecordsPage(recordsPage)
    autoCoder.setRecordsPerPage(recordsPerPage)
  }, [recordsPage, recordsPerPage])

  // ThemeAI hook
  const themeAI = useThemeAI({ customer, job })
  // Dictation needs agent access and budget (audit C23); Theme AI opens below it.
  const dictation = useDictationAccess(customer)

  // Initialize themes from saved config when modal opens
  React.useEffect(() => {
    if (open && !initializedFromConfig) {
      const savedThemes = frameworkEngine?.themeai?.themes || []
      if (savedThemes.length > 0) {
        themeAI.setThemes(savedThemes)
        const savedMessages = frameworkEngine?.themeai?.messages || []
        if (savedMessages.length > 0) {
          const chatMessages = savedMessages.map(msg => ({
            role: msg.role as 'user' | 'assistant' | 'system',
            content: msg.content,
          }))
          themeAI.extractChat.setMessages(chatMessages)
        }
      }
      setInitializedFromConfig(true)
    }
  }, [open, frameworkEngine, initializedFromConfig])

  // Reset initialization tracking when modal closes
  React.useEffect(() => {
    if (!open) {
      setInitializedFromConfig(false)
    }
  }, [open])

  // Sync data to themeAI
  React.useEffect(() => {
    if (open && hasDataConfirmed) {
      const effectiveVariable: SourceVariable = variable || {
        name: 'source_text',
        folder_path: '',
        question: 'Source Text',
      }
      themeAI.setVariable(effectiveVariable)

      if (autoCoder.records.length > 0) {
        const recordsAsData: CarbonInterviewRawDataRow[] = autoCoder.records.map((r) => ({
          interview_id: r.interview_id,
          data: [{
            variable_id: effectiveVariable.name,
            variable_name: effectiveVariable.name,
            data: r.data?.source_text || '',
          }],
        }))
        themeAI.setData(recordsAsData)
      } else if (previewData.length > 0) {
        themeAI.setData(previewData)
      }
    }
  }, [open, hasDataConfirmed, variable, autoCoder.records, previewData])

  // Color map for theme visualization
  const colorMap = useMemo(() => {
    const themeNames = themeAI.themes.map(t => t.text || t.code)
    return buildColorMap(themeNames)
  }, [themeAI.themes])

  const extractChatMessages = useMemo(() => {
    return themeAI.extractChat.messages.map((msg) => ({
      role: msg.role,
      content: typeof msg.content === 'string' ? msg.content : '',
    })) as ChatMessage[]
  }, [themeAI.extractChat.messages])

  const handleSendMessage = async (model: string, input: string) => {
    await themeAI.chat(model, input)
  }

  const handleAddTheme = (theme: AutoCoderCode) => {
    themeAI.setThemes([...themeAI.themes, theme])
  }

  const handleUpdateTheme = (previousCode: string, theme: AutoCoderCode) => {
    themeAI.setThemes(themeAI.themes.map((t) => (t.code === previousCode ? theme : t)))
  }

  const handleDeleteTheme = (code: string) => {
    themeAI.setThemes(themeAI.themes.filter((t) => t.code !== code))
  }

  const handleApply = () => {
    // Build theme codes from current themes
    const themeCodes: AutoCoderCode[] = themeAI.themes.map(t => ({
      code: t.code || t.text,
      text: t.text || t.code,
      description: t.description,
    }))

    const themeFramework: AutoCoderFramework = {
      name: 'theme',
      main_variable: { name: 'theme', codes: themeCodes },
      suggestions: [],
      looped_variables: existingLoopedVariables || DEFAULT_LOOPED_VARIABLES,
    }

    // Persist engine config
    if (onFrameworkEngineChange) {
      const messages: GoChatMessage[] = themeAI.extractChat.messages.map((msg) => ({
        role: msg.role,
        content: typeof msg.content === 'string' ? msg.content : '',
      }))
      onFrameworkEngineChange({
        type: AutoCoderImportType.themeai,
        themeai: {
          themes: themeAI.themes,
          suggestions: [],
          messages,
          batch_size: frameworkEngine?.themeai?.batch_size || 10,
          prompt: frameworkEngine?.themeai?.prompt || '',
        },
      })
    }

    onApply(themeFramework)
  }

  const handleOpenSaveDialog = () => {
    setSaveLibraryName('theme')
    setSaveLibraryDescription('')
    setSaveDialogOpen(true)
  }

  const handleSaveToLibrary = async () => {
    if (!onSaveToLibrary || !saveLibraryName.trim()) return

    const themeCodes: AutoCoderCode[] = themeAI.themes.map(t => ({
      code: t.code || t.text,
      text: t.text || t.code,
      description: t.description,
    }))

    const data: FrameworkData = {
      name: saveLibraryName.trim(),
      main_variable: { name: saveLibraryName.trim().toLowerCase(), codes: themeCodes },
      suggestions: [],
      looped_variables: DEFAULT_LOOPED_VARIABLES,
    }

    await onSaveToLibrary({
      name: saveLibraryName.trim(),
      description: saveLibraryDescription.trim() || undefined,
      data,
    })

    setSaveDialogOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          height: '90vh',
          maxWidth: '95vw',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Theme AI - Extract Themes</Typography>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 2 }}>
        {!hasDataConfirmed ? (
          <Alert severity="warning" icon={<WarningIcon />}>
            Theme AI requires data to be confirmed first. Please go back to Step 2 and confirm your data source.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            {/* Data Preview */}
            <Box sx={{ flex: 0.8, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <DataPreviewTable
                records={autoCoder.records}
                totalCount={autoCoder.totalRecords}
                page={recordsPage}
                rowsPerPage={recordsPerPage}
                isLoading={autoCoder.recordsIsLoading}
                error={autoCoder.recordsError?.message}
                onPageChange={setRecordsPage}
                onRowsPerPageChange={setRecordsPerPage}
              />
            </Box>

            {/* Chat */}
            <Box sx={{ flex: 1.2, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Paper sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Theme Extraction Chat
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mx: -2, mb: -2, overflow: 'hidden' }}>
                  <SidePaneChat
                    messages={toSidePaneMessages(extractChatMessages)}
                    // The model ChatSession sent with advanced mode off.
                    onSend={(text) => handleSendMessage('gpt-4.1', text)}
                    loading={themeAI.extractIsLoading}
                    loadingTitle="Reading the responses and working on the themes…"
                    error={themeAI.extractChat.error}
                    placeholder="Ask questions about this data to extract themes..."
                    prompts={themeAI.examplePrompts}
                    disabled={disabled}
                    transcribeEndpoint={buildTranscribeEndpoint(customer, job)}
                    dictationDisabledReason={dictation.reason}
                    authHeader={typeof axios.defaults.headers.common['Authorization'] === 'string' ? axios.defaults.headers.common['Authorization'] as string : undefined}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Theme Editor */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Paper sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <ThemeEditor
                    themes={themeAI.themes}
                    isLoading={themeAI.extractIsLoading}
                    error={themeAI.extractError}
                    colorMap={colorMap}
                    readOnly={disabled}
                    onAddTheme={handleAddTheme}
                    onUpdateTheme={handleUpdateTheme}
                    onDeleteTheme={handleDeleteTheme}
                  />
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {onSaveToLibrary && (
          <Button
            variant="outlined"
            color="info"
            startIcon={<SaveIcon />}
            onClick={handleOpenSaveDialog}
            disabled={disabled || themeAI.themes.length === 0 || saveToLibraryLoading}
          >
            Save to Library
          </Button>
        )}
        {/* data-voice="confirm": Douglas asks before pressing (#1363) — writes the framework onto the pipeline */}
        <Button
          variant="contained"
          color="info"
          onClick={handleApply}
          disabled={disabled || themeAI.themes.length === 0}
          data-voice="confirm"
        >
          Apply to Pipeline
        </Button>
      </DialogActions>

      {/* Save to Library name dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Code Frame to Library</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Save these {themeAI.themes.length} themes as a reusable code frame in the library.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Code Frame Name"
            value={saveLibraryName}
            onChange={(e) => setSaveLibraryName(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            value={saveLibraryDescription}
            onChange={(e) => setSaveLibraryDescription(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleSaveToLibrary}
            disabled={!saveLibraryName.trim() || saveToLibraryLoading}
          >
            {saveToLibraryLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default ThemeAIModal
